/**
 * 写操作对话框：**预演**与**结果**两个阶段复用同一个组件。
 *
 * 流程（对应后端四道闸门，客户端不绕过任何一层）：
 *   1. 预览（dry-run）：显示 `items[]` —— 哪些会执行（planned）、哪些被护栏拦下（rejected + 原因），
 *      以及将被发往 Meta 的请求（method / url / params）；
 *   2. 用户点「确认执行」才发真写（`dryRun:false, confirm:true`）；
 *   3. 结果：逐条 succeeded / failed / rejected，**失败与被拦下必须显示原因**，
 *      绝不把 rejected 记成成功。
 */

import type { ReactElement } from "react";
import { Modal } from "./Modal.js";
import { DASH } from "../lib/format.js";
import type {
  ActionItemResult,
  ActionItemStatus,
  ActionPlanResult,
  ActionRefreshResult,
  MetaActionName,
} from "../lib/apiTypes.js";

export const ACTION_LABEL: Readonly<Record<MetaActionName, string>> = {
  pause: "暂停",
  activate: "启动",
  set_daily_budget: "改日预算",
  set_lifetime_budget: "改总预算",
  // 2026-10-05 第二十二批：改名（`POST /{object_id}` + `{name}`）。
  rename: "改名",
};

/**
 * 预算归属层的中文短名（2026-10-04 第六批）。
 *
 * 它是**服务端查库**解析出来的结论（客户端无法提供），显示给用户是为了让
 * 「为什么这个按钮在这个层」有据可查。
 */
const BUDGET_SCOPE_LABEL: Readonly<Record<"campaign" | "adset" | "unknown", string>> = {
  campaign: "预算在系列层",
  adset: "预算在广告组层",
  unknown: "预算归属层未知",
};

interface StatusTone {
  readonly label: string;
  readonly className: string;
}

const STATUS_TONE: Readonly<Record<ActionItemStatus, StatusTone>> = {
  planned: { label: "将执行", className: "border-info/40 bg-info-subtle text-info-text" },
  succeeded: { label: "成功", className: "border-success/40 bg-success-subtle text-success-text" },
  failed: { label: "失败", className: "border-danger/40 bg-danger-subtle text-danger-text" },
  rejected: { label: "被拦下", className: "border-warning/40 bg-warning-subtle text-warning-text" },
};

/**
 * 一条护栏结论是不是**阻断**（2026-10-04 第六批）。
 *
 * ⚠️ 缺省（老后端没给 `severity`）一律按 `"blocking"` 处理：宁可保守，
 * 也不要把一条不知道严重性的拦截当成"可以继续"。
 */
function isBlockingIssue(issue: ActionItemResult["guardIssues"][number]): boolean {
  return issue.severity !== "warning";
}

function requestedParams(item: ActionItemResult): string {
  if (item.plannedRequest === null) return DASH;
  const entries = Object.entries(item.plannedRequest.params);
  if (entries.length === 0) return `${item.plannedRequest.method} ${item.plannedRequest.url}`;
  return `${item.plannedRequest.method} ${item.plannedRequest.url} · ${entries
    .map(([key, value]) => `${key}=${value}`)
    .join(" & ")}`;
}

/**
 * 把定向刷新结果压成一行可读文本（第十三批 · 用户第 1 条）。
 *
 * 只展示**真实回读**到的值；`refreshed:false` 走另一条失败文案，绝不在这里编数字。
 */
function refreshSummary(refresh: ActionRefreshResult): string {
  const parts: string[] = [];
  if (refresh.status !== null) {
    parts.push(`状态=${refresh.status}${refresh.effectiveStatus === null ? "" : `（${refresh.effectiveStatus}）`}`);
  }
  if (refresh.dailyBudgetCents !== null) parts.push(`日预算=${refresh.dailyBudgetCents} 分`);
  if (refresh.lifetimeBudgetCents !== null) parts.push(`总预算=${refresh.lifetimeBudgetCents} 分`);
  return parts.length === 0 ? "已写入我们自己的台账" : parts.join(" · ");
}

export interface ActionPlanDialogProps {
  readonly open: boolean;
  readonly mode: "preview" | "results";
  readonly title: string;
  readonly requestLabel: string;
  readonly plan: ActionPlanResult | null;
  readonly busy: boolean;
  /** 整次请求失败时的错误文本（例如 403 WRITES_DISABLED）。 */
  readonly errorMessage: string | null;
  /** 403 WRITES_DISABLED：显示「服务端未开启写操作」而不是普通错误。 */
  readonly writesDisabled: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

export function ActionPlanDialog({
  open,
  mode,
  title,
  requestLabel,
  plan,
  busy,
  errorMessage,
  writesDisabled,
  onConfirm,
  onClose,
}: ActionPlanDialogProps): ReactElement {
  const items = plan?.items ?? [];
  const summary = plan?.summary ?? null;
  const canConfirm = plan !== null && summary !== null && summary.planned > 0;
  // 第六批：把「非阻断警告」单独计数并显式提示——它**不**拦流程（当前只有 BUDGET_LEVEL_UNKNOWN）。
  const warningIssueCount = items.reduce(
    (sum, item) => sum + item.guardIssues.filter((issue) => !isBlockingIssue(issue)).length,
    0,
  );

  return (
    <Modal
      open={open}
      title={title}
      description={
        mode === "preview"
          ? `${requestLabel} · 这是 dry-run 预演：还没有向 Meta 发出任何请求`
          : `${requestLabel} · 真写结果`
      }
      onClose={busy ? () => undefined : onClose}
      footer={
        mode === "preview" ? (
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-control border border-line px-3 text-table text-ink transition-colors dur-fast hover:bg-hover disabled:opacity-40"
              style={{ height: "var(--control-height)" }}
            >
              取消
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy || !canConfirm}
              className="rounded-control bg-danger px-3 text-table font-medium text-ink-inverse transition-opacity dur-fast hover:opacity-90 disabled:opacity-40"
              style={{ height: "var(--control-height)" }}
            >
              {busy ? "提交中…" : canConfirm ? "确认执行（真写）" : "没有可执行的目标"}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="rounded-control border border-line px-3 text-table text-ink transition-colors dur-fast hover:bg-hover"
            style={{ height: "var(--control-height)" }}
          >
            关闭
          </button>
        )
      }
    >
      <div className="space-y-4">
        {errorMessage === null ? null : (
          <div className="rounded-control border border-danger/40 bg-danger-subtle px-3 py-2 text-table text-danger-text">
            <p className="font-medium">
              {writesDisabled ? "服务端未开启写操作" : "请求失败"}
            </p>
            <p className="mt-1 text-meta">{errorMessage}</p>
            {writesDisabled ? (
              <p className="mt-1 text-2xs">
                服务端需要 <span className="font-mono">AMS_ALLOW_META_WRITES=1</span> 并配置 Meta 令牌后才会执行真写；
                你可以继续用 dry-run 预演将要发生什么。
              </p>
            ) : null}
          </div>
        )}

        {warningIssueCount > 0 ? (
          <div
            className="rounded-control border border-warning/40 bg-warning-subtle px-3 py-2 text-meta text-warning-text"
            data-testid="action-warning-banner"
          >
            <p className="font-medium">有 {warningIssueCount} 条**非阻断**提示（黄色）：流程可以继续</p>
            <p className="mt-1 text-2xs">
              例如 <span className="font-mono">BUDGET_LEVEL_UNKNOWN</span>：客户端判据不足时后端仍放行，
              但会把「无法确认预算归属层」如实告诉你。真正的**阻断**（如
              <span className="font-mono"> BUDGET_LEVEL_MISMATCH</span>）是红色，且该项不会被执行。
            </p>
          </div>
        ) : null}

        {summary === null ? null : (
          <div className="flex flex-wrap gap-2 text-2xs">
            <span className="rounded-full border border-line bg-sunken px-2 py-0.5 text-ink-2 tnum">
              目标 {summary.total}
            </span>
            <span className="rounded-full border border-info/40 bg-info-subtle px-2 py-0.5 text-info-text tnum">
              {mode === "preview" ? "将执行" : "计划"} {summary.planned}
            </span>
            {mode === "results" ? (
              <>
                <span className="rounded-full border border-success/40 bg-success-subtle px-2 py-0.5 text-success-text tnum">
                  成功 {summary.succeeded}
                </span>
                <span className="rounded-full border border-danger/40 bg-danger-subtle px-2 py-0.5 text-danger-text tnum">
                  失败 {summary.failed}
                </span>
              </>
            ) : null}
            <span className="rounded-full border border-warning/40 bg-warning-subtle px-2 py-0.5 text-warning-text tnum">
              被拦下 {summary.rejected}
            </span>
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-meta text-ink-3">没有逐条结果。</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-table">
              <thead>
                <tr className="border-b border-line-subtle text-left text-meta text-ink-3">
                  <th className="py-1 pr-3 font-medium">目标</th>
                  <th className="py-1 pr-3 font-medium">动作</th>
                  <th className="py-1 pr-3 font-medium">结果</th>
                  <th className="py-1 font-medium">原因 / 将发送的请求</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const tone = STATUS_TONE[item.status];
                  const blockingIssues = item.guardIssues.filter((issue) => isBlockingIssue(issue));
                  const warningIssues = item.guardIssues.filter((issue) => !isBlockingIssue(issue));
                  const scope = item.budgetScope;
                  return (
                    <tr key={`${item.level}:${item.entityId}:${item.action}`} className="border-b border-line-subtle align-top">
                      <td className="py-1.5 pr-3 text-ink">
                        <span className="font-mono text-2xs text-ink-3">{item.level}</span>
                        <br />
                        <span className="font-mono text-2xs">{item.entityId}</span>
                        {scope === undefined || scope === null ? null : (
                          <span
                            className="mt-1 inline-flex rounded-full border border-line bg-sunken px-1.5 py-0.5 text-2xs text-ink-2"
                            data-budget-scope={scope}
                            title="服务端查库解析的预算归属层；错层会被 BUDGET_LEVEL_MISMATCH 拦下"
                          >
                            {BUDGET_SCOPE_LABEL[scope]}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3 text-ink-2">{ACTION_LABEL[item.action]}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`inline-flex rounded-full border px-1.5 py-0.5 text-2xs ${tone.className}`}>
                          {tone.label}
                        </span>
                      </td>
                      <td className="py-1.5 text-meta text-ink-2">
                        {/*
                          reason 往往就是第一条阻断 issue 的 message（服务端取法见 actions.ts）。
                          已经逐条列出时不再重复一遍，避免同一个原因出现两次。
                        */}
                        {item.reason === null ||
                        item.guardIssues.some((issue) => issue.message === item.reason) ? null : (
                          <p>{item.reason}</p>
                        )}
                        {/*
                          阻断（红色）与警告（黄色）**视觉分开**（第六批）：
                          阻断来自服务端闸门（如 BUDGET_LEVEL_MISMATCH），message 原样展示，不吞成"失败"。
                        */}
                        {blockingIssues.length === 0 ? null : (
                          <ul
                            className="mt-1 list-disc rounded-control border border-danger/40 bg-danger-subtle py-1 pl-6 pr-2 text-2xs text-danger-text"
                            data-testid="guard-blocking"
                          >
                            {blockingIssues.map((issue) => (
                              <li key={`${issue.code}:${issue.message}`}>
                                <span className="font-mono font-semibold">{issue.code}</span>
                                <span className="ml-1">（阻断）</span>：{issue.message}
                              </li>
                            ))}
                          </ul>
                        )}
                        {warningIssues.length === 0 ? null : (
                          <ul
                            className="mt-1 list-disc rounded-control border border-warning/40 bg-warning-subtle py-1 pl-6 pr-2 text-2xs text-warning-text"
                            data-testid="guard-warning"
                          >
                            {warningIssues.map((issue) => (
                              <li key={`${issue.code}:${issue.message}`}>
                                <span className="font-mono font-semibold">{issue.code}</span>
                                <span className="ml-1">（非阻断警告）</span>：{issue.message}
                              </li>
                            ))}
                          </ul>
                        )}
                        {item.plannedRequest === null ? null : (
                          <p className="mt-1 break-all font-mono text-2xs text-ink-3">{requestedParams(item)}</p>
                        )}
                        {/*
                          第十三批（用户第 1 条）：写成功后**是否已按 Meta 回读刷新**。
                          成功项必须显示「已刷新 + 真实新值」；失败项如实写原因 + 稍后自动同步，
                          绝不把「未刷新」显示成「已改成新值」。
                        */}
                        {mode === "results" && item.refresh != null ? (
                          item.refresh.refreshed ? (
                            <p className="mt-1 text-2xs text-success-text" data-testid="action-refresh-ok">
                              已按 Meta 回读刷新我们自己的台账：{refreshSummary(item.refresh)}
                            </p>
                          ) : (
                            <p className="mt-1 text-2xs text-warning-text" data-testid="action-refresh-failed">
                              未能立即刷新：{item.refresh.refreshError ?? "原因未返回"}（页面稍后自动同步）
                            </p>
                          )
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {mode === "preview" && plan !== null && plan.summary.planned === 0 ? (
          <p className="rounded-control border border-warning/40 bg-warning-subtle px-3 py-2 text-meta text-warning-text">
            所有目标都被护栏拦下：不会发出任何写请求。请修正预算或选择其它目标后重试。
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
